import { Component, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { UIGridBaseComponent } from 'app/ui/ui-grid-base.component';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AppService } from 'app/app.service';
import { ApiService } from 'app/api.service';
import { ModalActivityLogComponent } from 'app/modal/modal-activity-log/modal-activity-log.component';
import { ModalProjectExportComponent } from 'app/modal/modal-project-exporter/modal-project-exporter.component';
import { Subscription } from 'rxjs';
import { ModalQuoteChooseProjectComponent } from 'app/modal/modal-quote-choose-project/modal-quote-choose-project.component';
import { WarehouseService } from 'app/warehouse/warehouse.service';
import { ModalAlertInboxComponent } from 'app/alert/modal-alert-inbox/modal-alert-inbox.component';
import { ModalCustomiseSidebarComponent } from 'app/modal/modal-customise-sidebar/modal-customise-sidebar.component';
import { sendDealProjectChange } from 'app/quote/quote-deal-project';

@Component({
	selector: 'app-core-navigation',
	templateUrl: './core-navigation.component.html',
	styleUrls: ['./core-navigation.component.scss']
})
export class CoreNavigationComponent extends UIGridBaseComponent implements OnChanges, OnDestroy {

	@Input() org;

	@ViewChild(ModalActivityLogComponent) activityLogModal: ModalActivityLogComponent;
	@ViewChild(ModalProjectExportComponent) projectExportModal: ModalProjectExportComponent;
	@ViewChild(ModalQuoteChooseProjectComponent) chooseProjectModal: ModalQuoteChooseProjectComponent;
	@ViewChild(ModalAlertInboxComponent) alertInboxModal: ModalAlertInboxComponent;
	@ViewChild(ModalCustomiseSidebarComponent) customiseSidebarModal: ModalCustomiseSidebarComponent;

	navigation: any = null;
	hovered = null;

	// Main-menu items split for reordering: Dashboard stays pinned at the top, Discover
	// pinned at the bottom, and the middle block is what the Customise sidebar popup sorts.
	topItems: any[] = [];
	mainItems: any[] = [];
	footerItems: any[] = [];
	canReorder = false;

	quoteSubscription: Subscription;
	projectSubscription: Subscription;
	quoteStageSubscription: Subscription;
	routerSubscription: Subscription;

	quoteNoteCount = 0;
	billableTaskCount = 0;
	projectNoteCount = 0;
	followupTaskCount = 0;
	requestsToApproveCount = 0;
	// CRM-107. Open deals only — a badge counting Won and Lost tells nobody anything.
	crmOpenDealCount = 0;
	isOnboardingNew = false;

	expandedGroups = new Set<string>();
	collapsedGroups = new Set<string>();

	quoteInfo = null;
	showInvoicing = false;
	
	mainMenuIcons = {
		overview: 'dashboard',
		alert: 'notifications',
		crm: 'crm',
		'quote-sales': 'quotes',
		projects: 'projects',
		catalogues: 'catalogue',
		procurement: 'procurement',
		inventory: 'inventory',
		workhub: 'workhub',
		customer: 'customers'
	};

	constructor(
		public app: AppService,
		private api: ApiService,
		public route: ActivatedRoute,
		private router: Router,
		public warehouseService: WarehouseService
	) {
		super();

		this.refreshQuoteNotes();
		this.refreshProjectNotes();
		this.refreshQuoteNavigation();

		this.app.quoteNavigationChange.subscribe(() => {
			this.quoteNoteCount = 0;
			this.showInvoicing = false;
			this.refreshQuoteNotes();
			this.refreshQuoteNavigation();
		});

		this.app.projectNavigationChange.subscribe(() => {
			this.projectNoteCount = 0;
			this.refreshProjectNotes();
		});

		this.quoteStageSubscription = this.app.quoteStageChange.subscribe(() => {
			this.refreshQuoteNavigation();
		});

		// Keep the group containing the active route expanded as the user navigates.
		this.routerSubscription = this.router.events.subscribe(event => {
			if (event instanceof NavigationEnd) this.expandActiveGroups();
		});
	}

	ngOnDestroy(): void {
		if (this.quoteSubscription) this.quoteSubscription.unsubscribe();
		if (this.projectSubscription) this.projectSubscription.unsubscribe();
		if (this.quoteStageSubscription) this.quoteStageSubscription.unsubscribe();
		if (this.routerSubscription) this.routerSubscription.unsubscribe();
	}

	ngOnChanges() {
		this.refresh();
	}

	refresh() {
		if (this.app.org) {
			this.api.organisation.navigation(data => {
				this.navigation = data;
				this.isOnboardingNew = !!data?.new_onboarding;
				this.canReorder = !!data?.can_reorder;
				this.partitionMainItems();
				this.expandActiveGroups();
			}, () => {
				this.navigation = null;
				this.isOnboardingNew = false;
				this.canReorder = false;
				this.partitionMainItems();
			});
		} else {
			this.navigation = null;
			this.canReorder = false;
			this.partitionMainItems();
		}
	}

	// Split the main list into: leading pinned (Dashboard), the reorderable middle block,
	// and trailing pinned (Discover). Only the middle block is offered to the popup.
	partitionMainItems() {
		this.topItems = [];
		this.mainItems = [];
		this.footerItems = [];
		let seen = false;
		this.getNavigation('main').forEach(item => {
			if (item.pinned) {
				if (seen) this.footerItems.push(item);
				else this.topItems.push(item);
			} else {
				seen = true;
				this.mainItems.push(item);
			}
		});
	}

	// The popup reorders and saves; it's handed what the sidebar has already resolved - icon included - rather than
	// working the menu out for itself.
	customiseSidebar() {
		this.customiseSidebarModal.open({
			items: this.mainItems.map(item => ({
				key: item.key,
				description: item.description,
				iconUrl: this.mainIcon(item),
				icon: item.icon
			}))
		});
	}

	/**
	 * Applies the order the popup saved, so the sidebar re-sorts without a re-fetch. Closing without saving passes
	 * nothing and leaves the menu alone.
	 *
	 * Anything the popup didn't name keeps its place at the end - the saved order is a list of keys, and a menu that
	 * gained an item since the popup opened shouldn't lose it.
	 */
	onSidebarCustomised(order) {
		if (!order?.length) return;

		const remaining = this.mainItems.slice();
		const sorted = [];

		order.forEach(key => {
			const index = remaining.findIndex(item => item.key === key);
			if (index !== -1) sorted.push(remaining.splice(index, 1)[0]);
		});

		this.mainItems = sorted.concat(remaining);
		if (this.navigation) this.navigation.main = this.topItems.concat(this.mainItems, this.footerItems);
	}

	isModuleSelected(module) {
		return this.app.routeData.module === module;
	}

	openAlerts() {
		if (this.app.routeData.module === 'alert') return;
		this.alertInboxModal.open();
	}

	isPathSelected(path) {
		return this.app.routeData.path === path || (!this.app.routeData.path && !path);
	}

	isSubpathSelected(path) {
		return this.app.routeData.subpath === path || (!this.app.routeData.subpath && !path);
	}

	getNavigation(category) {
		return (this.navigation ? this.navigation[category] : null) || [];
	}

	mainIcon(item) {
		const name = this.mainMenuIcons[item.key || item.path];
		return name ? `assets/icons/${name}.svg` : null;
	}

	// Router link array for a (possibly multi-segment) menu path, e.g. 'project/invoices'.
	linkRoute(item) {
		return ['/' + this.app.org].concat((item.path || '').split('/').filter(Boolean));
	}

	// Active state for a plain top-level link (matches the current module, as before).
	isTopLinkActive(item) {
		return this.app.routeData.module === (item.path || '').split('/')[0];
	}

	isChildActive(item) {
		const segments = (item.path || '').split('/').filter(Boolean);
		const module = segments[0] || '';
		const path = segments[1] || '';
		const sub = this.app.routeData.path || this.app.routeData.tab || '';
		return this.app.routeData.module === module && sub === path;
	}

	isGroupActive(group) {
		return (group.items || []).some(child => this.isChildActive(child));
	}

	expandActiveGroups() {
		this.getNavigation('main').forEach(item => {
			if (item.type === 'group' && this.isGroupActive(item)) this.expandedGroups.add(item.key);
		});

		const category = this.app.routeData.navigation;
		if (category === 'settings' || category === 'purchase' || category === 'warehouse') {
			this.getNavigation(category).forEach(item => {
				if (item.type === 'link' && item.items && item.items.length > 1 && this.isPathSelected(item.path)) {
					this.expandedGroups.add('sub:' + item.path);
				}
			});
		}
	}

	isGroupExpanded(group) {
		if (this.collapsedGroups.has(group.key)) return false;
		return this.expandedGroups.has(group.key) || this.isGroupActive(group);
	}

	toggleGroup(group) {
		if (this.isGroupExpanded(group)) {
			this.expandedGroups.delete(group.key);
			this.collapsedGroups.add(group.key);
		} else {
			this.expandedGroups.add(group.key);
			this.collapsedGroups.delete(group.key);
		}
	}

	isSubNavExpanded(item) {
		const key = 'sub:' + item.path;
		if (this.collapsedGroups.has(key)) return false;
		return this.expandedGroups.has(key) || this.isPathSelected(item.path);
	}

	toggleSubNav(item) {
		const key = 'sub:' + item.path;
		if (this.isSubNavExpanded(item)) {
			this.expandedGroups.delete(key);
			this.collapsedGroups.add(key);
		} else {
			this.expandedGroups.add(key);
			this.collapsedGroups.delete(key);
		}
	}

	isSubItemActive(item, subitem) {
		return this.isPathSelected(item.path) && this.isSubpathSelected(subitem.path);
	}

	getNavigationTutorialStep(path) {
		if (path === 'catalogue') return 101;
		if (path === 'configure') return 201;
		if (path === 'system') return 206;
		if (path === 'configure/labour') return 301;
		if (path === 'quote') return 401;
		return null;
	}

	getQuoteTutorialStep(path) {
		if (path === 'summary') return 502;
		if (path === 'editor') return 601;
		if (path === 'adjustments') return 701;
		if (path === 'view-proposal') return 801;
		return null;
	}

	refreshQuoteNotes() {
		const quoteId = this.app.routeParams.quote;

		if (!quoteId) {
			this.quoteNoteCount = 0;
			return;
		}

		this.api.activity.count(quoteId, 'quote', n => {
			if (quoteId === this.app.routeParams.quote) this.quoteNoteCount = n || 0;
		});
	}

	refreshQuoteNavigation() {
		const quoteId = this.app.routeParams.quote;
		this.showInvoicing = false;
		if (!quoteId) return;

		if (this.app.routeData.module === 'sales-order' && this.app.routeData.navigation === 'quote') {
			this.api.quote.headerSummary(this.app.routeParams.quote, data => {
				this.quoteInfo = data;
				if (this.quoteInfo.stage === 'complete' && this.app.routeData.module === 'sales-order') {
					this.showInvoicing = true;
				}
			}, () => {
				this.showInvoicing = false;
			});
		}
	}

	refreshBillableTaskCount() {
		this.api.task.billableCount(n => {
			this.billableTaskCount = n || 0;
		});
	}

	refreshFollowUpTaskCount() {
		this.api.task.followUpCount(n => {
			this.followupTaskCount = n || 0;
		});
	}

	refreshRequestToApproveCount() {
		this.api.inventory.requestToApproveCount(n => {
			this.requestsToApproveCount = n || 0;
		});
	}

	refreshCrmOpenDealCount() {
		if (!this.app.crmEnabled) {
			this.crmOpenDealCount = 0;
			return;
		}
		this.api.crm.openDealCount(n => {
			this.crmOpenDealCount = n || 0;
		}, () => { this.crmOpenDealCount = 0; });
	}

	openQuoteNotes() {
		if (!this.app.routeParams.quote) return;

		this.activityLogModal.open({
			id: this.app.routeParams.quote,
			type: 'quote'
		});
	}

	openQuoteInvoicing() {
		if (this.app.routeData.module === 'sales-order' && this.app.routeData.navigation === 'quote' && this.app.routeParams.quote) {
			this.api.quote.headerSummary(this.app.routeParams.quote, data => {
				this.quoteInfo = data;
				if (!this.quoteInfo.project_id) {
					this.chooseProjectModal.open({
						customerId: this.quoteInfo.customer_id,
						currencyId: this.quoteInfo.currency_id
					});
				} else {
					this.router.navigate(['/', this.app.org, 'project', this.quoteInfo.project_id, 'invoicing']);
				}
			});
		}
	}

	chooseProjectModalClosed(data) {
		if (data && this.quoteInfo) {
			sendDealProjectChange(this.app, this.api.quote.setProject, { id: this.quoteInfo.id, project_id: data }, () => {
				this.router.navigate(['/', this.app.org, 'project', data, 'invoicing']);
			}, error => {
				this.app.notifications.showDanger(error.message);
			});
		}
	}

	refreshProjectNotes() {
		const projectId = this.app.routeParams.project;

		if (!projectId) {
			this.projectNoteCount = 0;
			return;
		}

		this.api.activity.count(projectId, 'project', n => {
			if (projectId === this.app.routeParams.project) this.projectNoteCount = n || 0;
		});
	}

	openProjectNotes() {
		if (!this.app.routeParams.project) return;

		this.activityLogModal.open({
			id: this.app.routeParams.project,
			type: 'project'
		});
	}

	openProjectExporter() {
		if (!this.app.routeParams.project) return;

		this.projectExportModal.open({
			context: 'project',
			id: this.app.routeParams.project
		});
	}

}
